import React, { useState, useEffect } from 'react';
import { useShop } from '../context/ShopContext';
import { 
  Sparkles, 
  ArrowRight, 
  ShieldCheck, 
  Award, 
  Truck, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight
} from 'lucide-react';

export default function HeroBanner() {
  const { 
    setActiveCategory, 
    heroBannerData,
    setIsWastageModalOpen
  } = useShop();

  const hero = heroBannerData || {};

  // Wide Landscape Image & Video Slider Presets (Full-width Royal Jewellery)
  const defaultSlides = [
    {
      id: 'slide-1',
      badge: '👑 ROYAL RAJPUTANA HERITAGE • 100% BIS 916 PURE GOLD',
      titleLine1: 'The Sovereign Splendour of',
      titleHighlight: 'Royal Rajputana Jewellery',
      subtitle: 'Experience generational royal craftsmanship with 100% BIS hallmarked 22KT gold, uncut polki chokers, and certified solitaires.',
      primaryBtnText: 'Explore Royal Collections',
      primaryBtnCategory: 'all',
      secondaryBtnText: 'Live Gold Rate Calculator',
      video: '/images/jewelry-hero.mp4',
      videoUrl: 'https://assets.mixkit.co/videos/20877/20877-720.mp4',
      image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1920&q=85'
    },
    {
      id: 'slide-2',
      badge: '✨ CERTIFIED SOLITAIRES & POLKI • 0% MIDDLEMEN MARKUP',
      titleLine1: 'Eternal Sparkle of Certified Solitaires &',
      titleHighlight: 'Artisan Heirloom Gold',
      subtitle: 'Direct workshop pricing with complete transparency, laser-inscribed HUID hallmark purity, and insured armored nationwide transit.',
      primaryBtnText: 'View New Arrivals',
      primaryBtnCategory: 'all',
      secondaryBtnText: 'Live Gold Rate Calculator',
      video: '/images/jewelry-model.mp4',
      videoUrl: 'https://assets.mixkit.co/videos/34427/34427-720.mp4',
      image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1920&q=85'
    },
    {
      id: 'slide-3',
      badge: '💎 IMPERIAL BRIDAL TROUSSEAU 2026',
      titleLine1: 'Imperial Wedding Jewellery &',
      titleHighlight: 'Bespoke Masterpieces',
      subtitle: 'Handcrafted Padmavati chokers, regal bridal necklaces and heritage kadas made to perfection for your unforgettable celebrations.',
      primaryBtnText: 'Explore All Collections',
      primaryBtnCategory: 'all',
      secondaryBtnText: 'Live Gold Rate Calculator',
      image: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=1920&q=85'
    }
  ];

  // Resolve slides: keep video if available, fallback to default slides
  const rawSlides = Array.isArray(hero.slides) && hero.slides.length > 0 
    ? hero.slides 
    : defaultSlides;

  const slides = rawSlides.map((s, idx) => ({
    id: s.id || `slide-${idx}`,
    badge: s.badge !== undefined ? s.badge : (defaultSlides[idx % defaultSlides.length]?.badge || ''),
    titleLine1: s.titleLine1 !== undefined ? s.titleLine1 : (defaultSlides[idx % defaultSlides.length]?.titleLine1 || ''),
    titleHighlight: s.titleHighlight !== undefined ? s.titleHighlight : (defaultSlides[idx % defaultSlides.length]?.titleHighlight || ''),
    subtitle: s.subtitle !== undefined ? s.subtitle : (defaultSlides[idx % defaultSlides.length]?.subtitle || ''),
    primaryBtnText: s.primaryBtnText || 'Explore Collections',
    primaryBtnCategory: s.primaryBtnCategory || 'all',
    secondaryBtnText: s.secondaryBtnText || 'Live Gold Rate Calculator',
    video: s.video !== undefined ? s.video : defaultSlides[idx % defaultSlides.length]?.video,
    videoUrl: s.videoUrl !== undefined ? s.videoUrl : defaultSlides[idx % defaultSlides.length]?.videoUrl,
    image: s.image || s.fallbackImage || s.heroImage || defaultSlides[idx % defaultSlides.length]?.image
  }));

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Auto-slide transition every 5 seconds (pauses on hover)
  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [isPaused, slides.length, currentSlide]);

  const goToSlide = (idx) => {
    setCurrentSlide(idx);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Touch swipe handlers for mobile
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  const handleExplore = (category) => {
    setActiveCategory(category || 'all');
    const catalogEl = document.getElementById('catalog-section');
    if (catalogEl) {
      catalogEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const activeSlide = slides[currentSlide] || slides[0];

  return (
    <section 
      style={{
        position: 'relative',
        padding: '10px 0 12px',
        background: '#f1f3f6',
        overflow: 'hidden'
      }}
      aria-label="Royal Collections Showcase Slider"
    >
      <div className="container" style={{ position: 'relative' }}>

        {/* Wide Full-Width Panoramic Slider Card */}
        <div 
          className="hero-slider-card"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            position: 'relative',
            borderRadius: '4px',
            overflow: 'hidden',
            minHeight: 'clamp(230px, 24vw, 310px)',
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #e0e0e0',
            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.08)',
            backgroundColor: '#0c0b12'
          }}
        >
          {/* Background Images & Videos for all slides with smooth cross-fade */}
          {slides.map((s, idx) => {
            const isActive = idx === currentSlide;
            const hasVideo = Boolean(s.video || s.videoUrl);
            return (
              <div
                key={s.id || idx}
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: isActive ? 1 : 0,
                  transition: 'opacity 0.65s ease-in-out',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              >
                {hasVideo ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={s.image}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center 35%',
                      display: 'block'
                    }}
                  >
                    {s.video && <source src={s.video} type="video/mp4" />}
                    {s.videoUrl && <source src={s.videoUrl} type="video/mp4" />}
                    <img
                      src={s.image}
                      alt={s.titleLine1 || 'Royal Jewellery'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center 35%',
                        display: 'block'
                      }}
                    />
                  </video>
                ) : (
                  <img
                    src={s.image}
                    alt={s.titleLine1 || 'Royal Jewellery'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center 30%',
                      display: 'block'
                    }}
                    loading={idx === 0 ? 'eager' : 'lazy'}
                  />
                )}
              </div>
            );
          })}

          {/* Flipkart Soft Contrast Gradient Overlay for crystal-clear readability */}
          <div 
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.62) 42%, rgba(0, 0, 0, 0.2) 80%, rgba(0, 0, 0, 0.45) 100%)',
              zIndex: 2,
              pointerEvents: 'none'
            }}
          />

          {/* Slider Content Area (Overlaid on the wide banner) */}
          <div 
            key={currentSlide} 
            className="hero-slider-content"
            style={{
              position: 'relative',
              zIndex: 4,
              padding: 'clamp(1rem, 2.4vw, 1.8rem) clamp(1rem, 3.2vw, 2.5rem)',
              maxWidth: '680px',
              animation: 'sliderContentFade 0.4s ease forwards'
            }}
          >
            {/* Flipkart Yellow / Orange Deal Tag */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.55rem' }}>
              <div 
                className="hero-royal-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.22rem 0.65rem',
                  background: '#ffe500',
                  border: 'none',
                  borderRadius: '2px',
                  color: '#2874f0',
                  fontSize: 'clamp(0.66rem, 1.2vw, 0.74rem)',
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                }}
              >
                <Sparkles size={12} color="#2874f0" />
                {activeSlide.badge}
              </div>

              {(activeSlide.video || activeSlide.videoUrl) && (
                <span 
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(4px)',
                    color: '#ffffff',
                    fontSize: '0.64rem',
                    fontWeight: 700,
                    padding: '0.2rem 0.5rem',
                    borderRadius: '2px',
                    letterSpacing: '0.04em'
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00e676', display: 'inline-block' }}></span>
                  HD CINEMATIC
                </span>
              )}
            </div>

            {/* Main Title */}
            <h1 
              className="hero-slider-title"
              style={{
                fontSize: 'clamp(1.25rem, 2.6vw, 1.95rem)',
                lineHeight: 1.18,
                fontWeight: 800,
                color: '#ffffff',
                marginBottom: '0.45rem',
                letterSpacing: '-0.02em',
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)'
              }}
            >
              {activeSlide.titleLine1}{' '}
              <span style={{ color: '#ffe500', display: 'inline-block' }}>
                {activeSlide.titleHighlight}
              </span>
            </h1>

            {/* Subtitle */}
            <p 
              className="hero-slider-subtitle"
              style={{
                color: 'rgba(255, 255, 255, 0.92)',
                fontSize: 'clamp(0.76rem, 1.2vw, 0.88rem)',
                lineHeight: 1.45,
                marginBottom: '1rem',
                maxWidth: '540px',
                textShadow: '0 1px 4px rgba(0, 0, 0, 0.7)'
              }}
            >
              {activeSlide.subtitle}
            </p>

            {/* CTA Buttons - Flipkart Yellow Add/Explore & White Outline */}
            <div 
              className="hero-cta-wrap"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}
            >
              <button 
                onClick={() => handleExplore(activeSlide.primaryBtnCategory || 'all')}
                style={{
                  background: '#ff9f00',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '2px',
                  fontSize: '0.84rem',
                  padding: '0.55rem 1.35rem',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
                  textTransform: 'uppercase'
                }}
              >
                {activeSlide.primaryBtnText} <ArrowRight size={15} />
              </button>

              <button
                onClick={() => {
                  if (setIsWastageModalOpen) {
                    setIsWastageModalOpen(true);
                  } else {
                    handleExplore('all');
                  }
                }}
                style={{
                  fontSize: '0.84rem',
                  padding: '0.55rem 1.2rem',
                  fontWeight: 600,
                  background: 'rgba(255, 255, 255, 0.18)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.7)',
                  color: '#ffffff',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  textTransform: 'uppercase'
                }}
              >
                {activeSlide.secondaryBtnText}
              </button>
            </div>
          </div>

          {/* Left Arrow Button */}
          {slides.length > 1 && (
            <button
              type="button"
              onClick={prevSlide}
              aria-label="Previous Slide"
              style={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 6,
                width: '36px',
                height: '68px',
                borderRadius: '0 4px 4px 0',
                background: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                color: '#212121',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
              }}
            >
              <ChevronLeft size={22} />
            </button>
          )}

          {/* Right Arrow Button */}
          {slides.length > 1 && (
            <button
              type="button"
              onClick={nextSlide}
              aria-label="Next Slide"
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 6,
                width: '36px',
                height: '68px',
                borderRadius: '4px 0 0 4px',
                background: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                color: '#212121',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
              }}
            >
              <ChevronRight size={22} />
            </button>
          )}

          {/* Bottom Indicators */}
          <div 
            style={{
              position: 'absolute',
              bottom: '12px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 6,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {slides.map((s, idx) => {
              const isActive = currentSlide === idx;
              return (
                <button
                  key={s.id || idx}
                  type="button"
                  onClick={() => goToSlide(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  style={{
                    width: isActive ? '22px' : '7px',
                    height: '7px',
                    borderRadius: '4px',
                    background: isActive ? '#2874f0' : 'rgba(255, 255, 255, 0.65)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    padding: 0
                  }}
                />
              );
            })}
          </div>

        </div>

        {/* 4 Pillars Trust Bar Below Slider - Pure White Flipkart Strip */}
        <div 
          className="hero-trust-bar"
          style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginTop: '10px',
          background: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: '2px',
          padding: '12px 18px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: '#e8f0fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <ShieldCheck size={18} color="#2874f0" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#212121' }}>100% BIS Hallmarked</div>
              <div style={{ fontSize: '0.72rem', color: '#878787' }}>Government Assayed with HUID</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: '#e8f0fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Award size={18} color="#2874f0" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#212121' }}>Certified Solitaires</div>
              <div style={{ fontSize: '0.72rem', color: '#878787' }}>IGI & SGL Authenticated</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: '#e8f0fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Truck size={18} color="#2874f0" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#212121' }}>Free Armored Transit</div>
              <div style={{ fontSize: '0.72rem', color: '#878787' }}>Insured to 19,000+ Pincodes</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: '#e8f0fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <RefreshCw size={18} color="#2874f0" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#212121' }}>Lifetime Buyback</div>
              <div style={{ fontSize: '0.72rem', color: '#878787' }}>100% Bullion Value Guarantee</div>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        @keyframes sliderContentFade {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (max-width: 768px) {
          .hero-slider-card {
            min-height: 240px !important;
          }
          .hero-slider-content {
            padding: 1rem 0.85rem !important;
          }
          .hero-royal-badge {
            font-size: 0.62rem !important;
            padding: 0.2rem 0.5rem !important;
            margin-bottom: 0.4rem !important;
          }
          .hero-slider-title {
            font-size: 1.25rem !important;
            line-height: 1.2 !important;
            margin-bottom: 0.4rem !important;
          }
          .hero-slider-subtitle {
            font-size: 0.72rem !important;
            line-height: 1.35 !important;
            margin-bottom: 0.75rem !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 2 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
          }
          .hero-cta-wrap {
            gap: 0.4rem !important;
          }
          .hero-cta-wrap button {
            padding: 0.45rem 0.85rem !important;
            font-size: 0.76rem !important;
          }
        }
        @media (max-width: 580px) {
          .hero-slider-card {
            min-height: 220px !important;
          }
          .hero-trust-bar {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.65rem !important;
            padding: 0.75rem !important;
          }
        }
      `}</style>
    </section>
  );
}
